'use client';

import { useRouter } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Button } from '~/components/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '~/components/ui/input-otp';
import { useAuth } from '~/contexts/auth/auth-context';

interface OTPVerifyProps {
  method: 'email' | 'phone';
  contact: string;
  onBack: () => void;
}

export function OTPVerify({ method, contact, onBack }: OTPVerifyProps) {
  const { sendOTP, login } = useAuth();

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const { navigate } = useRouter();

  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((t) => t - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleVerify = useCallback(async () => {
    if (otp.length < 6) {
      setError('Ingresa el código completo');
      return;
    }
    setLoading(true);
    setError('');
    // Simulate verification delay
    await new Promise((r) => setTimeout(r, 1200));
    if (otp === '000000') {
      setError('Código incorrecto. Intenta de nuevo.');
      setLoading(false);
      return;
    }

    const data = method === 'email' ? { email: contact } : { phone: contact };
    await login({ code: otp, ...data });
    navigate({ to: '/', replace: true });
  }, [otp, login, method, navigate, contact]);

  async function handleResend() {
    await sendOTP(method, contact);
    setResendTimer(60);
    setOtp('');
    setError('');
  }

  return (
    <div className="flex flex-col gap-7">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        aria-label="Volver"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver
      </button>

      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-bold tracking-tight text-foreground">
          Verificar código
        </h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Enviamos un código de 6 dígitos a{' '}
          <span className="font-medium text-foreground">{contact}</span>
        </p>
      </div>

      <div className="flex flex-col items-center gap-4">
        <InputOTP
          maxLength={6}
          value={otp}
          onChange={(val) => {
            setOtp(val);
            setError('');
          }}
          autoFocus
        >
          <InputOTPGroup className="gap-2">
            <InputOTPSlot index={0} className="h-12 w-11 text-lg" />
            <InputOTPSlot index={1} className="h-12 w-11 text-lg" />
            <InputOTPSlot index={2} className="h-12 w-11 text-lg" />
            <InputOTPSlot index={3} className="h-12 w-11 text-lg" />
            <InputOTPSlot index={4} className="h-12 w-11 text-lg" />
            <InputOTPSlot index={5} className="h-12 w-11 text-lg" />
          </InputOTPGroup>
        </InputOTP>

        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>

      <Button
        onClick={handleVerify}
        disabled={loading || otp.length < 6}
        className="h-12 w-full rounded-2xl text-sm font-medium"
      >
        {loading ? 'Verificando...' : 'Verificar'}
      </Button>

      <div className="text-center">
        {resendTimer > 0 ? (
          <p className="text-xs text-muted-foreground">
            Reenviar código en{' '}
            <span className="font-medium tabular-nums text-foreground">
              {resendTimer}s
            </span>
          </p>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            className="text-xs font-medium text-foreground underline underline-offset-2"
          >
            Reenviar código
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={onBack}
        className="text-center text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
      >
        Cambiar método de inicio de sesión
      </button>
    </div>
  );
}
